import { useCallback, useMemo, useEffect, useRef } from "react";

type AnyFunction = (...args: any[]) => void;
/**
 * call onChange fn immediately and debounced save function after certain timeout
 * @param delayFn fn to call
 * @param [timeout=3000] timeout ms, default 3000ms
 */
export default function useDelayDebouncedFn<T extends AnyFunction[]>(
  monitorFns: T,
  delayFn: AnyFunction,
  delay = 5000,
) {
  const monitorFnsRef = useRef(monitorFns);
  const delayFnRef = useRef(delayFn);

  useEffect(() => {
    monitorFnsRef.current = monitorFns;
  }, [monitorFns]);

  useEffect(() => {
    delayFnRef.current = delayFn;
  }, [delayFn]);

  // use a ref to hold the latest delayFn value
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const { current: timer } = timerRef;
    // clean up side effects before unmount
    return () => timer && clearTimeout(timer);
  }, [timerRef]);

  const fnWithDelayDebounced = useCallback(
    (fns: T) =>
      fns.map((fn) => (...args: Parameters<typeof fn>) => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
        timerRef.current = setTimeout(() => {
          delayFnRef.current();
        }, delay);

        return fn(...args);
      }),
    [delayFnRef, delay],
  );

  return useMemo(
    () => fnWithDelayDebounced(monitorFnsRef.current) as typeof monitorFns,
    [fnWithDelayDebounced],
  );
}
